/**
 * Detect private / non-publicly-routable literal IP addresses.
 *
 * A store running cloud inference is reached by the cloud aggregator over the
 * public internet, so an RFC1918 / loopback / link-local / IPv6-ULA address can
 * never work. This mirrors the backend rule (PLA-2045) which uses Python's
 * `ipaddress.is_private`. It is intentionally a *subset* of that predicate: it
 * only flags clearly-private ranges, so the client never over-blocks something
 * the backend would accept. The backend remains the source of truth.
 *
 * Hostnames are NOT resolved (returns false): only literal IPs are judged.
 * Format placeholders (`{}`, `{0}`) are substituted with a test octet so
 * templated hosts like `192.168.1.{}` are evaluated on their fixed prefix.
 */

/** Replace Python-style format placeholders with a neutral octet. */
function substitutePlaceholders(value) {
  return value.replace(/\{[^}]*\}/g, "1");
}

/**
 * @param {string | undefined | null} value Raw device IP / host (may contain `{}`).
 * @returns {boolean} true if `value` is a literal private IP address.
 */
export default function isPrivateIp(value) {
  if (!value) {
    return false;
  }

  let host = substitutePlaceholders(String(value).trim());

  // Strip IPv6 brackets and any zone id (e.g. "[fe80::1%eth0]").
  if (host.startsWith("[") && host.endsWith("]")) {
    host = host.slice(1, -1);
  }
  host = host.split("%")[0];

  const ipv4 = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (ipv4) {
    const octets = ipv4.slice(1).map(Number);
    if (octets.some((n) => n > 255)) {
      return false; // Not a valid IPv4 → not our concern (regex validation handles it).
    }
    const [a, b] = octets;
    if (a === 10) return true; // 10.0.0.0/8
    if (a === 127) return true; // 127.0.0.0/8 loopback
    if (a === 192 && b === 168) return true; // 192.168.0.0/16
    if (a === 172 && b >= 16 && b <= 31) return true; // 172.16.0.0/12
    if (a === 169 && b === 254) return true; // 169.254.0.0/16 link-local
    return false;
  }

  if (host.includes(":")) {
    const lower = host.toLowerCase();
    if (lower === "::1") return true; // loopback
    if (lower.startsWith("fe80")) return true; // link-local
    if (/^f[cd][0-9a-f]{0,2}:/.test(lower)) return true; // fc00::/7 ULA
    return false;
  }

  return false; // hostname — never resolved
}
